import { useEffect, useRef, useState } from 'preact/hooks';
import { t } from '../../i18n/index.js';
import { playSound } from '../../audio/sounds.js';
import { TurnStatus } from '../common/TurnStatus.js';
import { MemoryIcon } from './MemoryIcons.js';
import { MEMORY_CARDS, MEMORY_PAIRS, type MemoryLastMove, type MemoryView } from '../../../shared/games/memory/index.js';
import type { ArenaResult, GameStateView } from '../../../shared/protocol.js';
import type { ArenaSocket } from '../../realtime.js';

/** How long a non-matching pair stays visible before it turns back (suunnitelma.md section 42). */
const MISMATCH_VISIBLE_MS = 1500;

/** Memory UI: 6 × 6 cards with flip animation. */
export function MemoryGame(props: { state: GameStateView<MemoryView>; socket: ArenaSocket; serverNow: number }) {
  const { state, socket } = props;
  const view = state.module;
  const myTurn = state.turn?.userId === state.me.id;
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const last = state.lastMove?.data as MemoryLastMove | undefined;

  // A mismatched pair is shown briefly to both players, then turned back.
  const [peek, setPeek] = useState<Map<number, number>>(new Map());
  const lastKey = last ? `${state.lastMove!.byUserId}:${last.step}:${last.index}:${last.first?.index ?? ''}` : '';
  const first = useRef(true);
  useEffect(() => {
    if (first.current || !last) {
      first.current = false;
      return;
    }
    playSound('cardFlip');
    if (last.step === 'second' && last.match) playSound('pairFound', 250);
    if (last.step === 'second' && !last.match && last.first) {
      setPeek(
        new Map([
          [last.first.index, last.first.symbol],
          [last.index, last.symbol],
        ]),
      );
      const timer = setTimeout(() => setPeek(new Map()), MISMATCH_VISIBLE_MS);
      return () => clearTimeout(timer);
    }
    // Any new flip ends the previous peek.
    setPeek(new Map());
  }, [lastKey]);

  const flip = (index: number) => {
    if (!myTurn || pending || view.cards[index] !== null) return;
    setPending(true);
    setError(null);
    setPeek(new Map());
    socket.emit('game:move', { index }, (r: ArenaResult) => {
      setPending(false);
      if (!r.ok) setError(t.arena.errors[r.error]);
    });
  };

  const found = view.pairs.me + view.pairs.opponent;
  const resultNote =
    last?.step === 'second' ? (
      <span class={`last-shot ${last.match ? 'shot-hit' : 'shot-miss'}`}>{last.match ? t.memory.match : t.memory.noMatch}</span>
    ) : null;

  return (
    <div class="memory">
      <TurnStatus state={state} serverNow={props.serverNow}>
        {resultNote}
      </TurnStatus>
      {!state.opponent.online && <div class="conn-banner">{t.battleship.opponentOffline}</div>}
      {error && <p class="error">{error}</p>}

      <div class="memory-layout">
        <div class={`memory-board ${myTurn && !pending ? 'active' : ''}`}>
          {Array.from({ length: MEMORY_CARDS }, (_, i) => {
            const symbol = view.cards[i] ?? peek.get(i) ?? null;
            const owner = view.owner[i];
            const open = symbol !== null;
            return (
              <button
                class={`m-card ${open ? 'open' : ''} ${owner ? `found found-${owner}` : ''} ${peek.has(i) ? 'peek' : ''}`}
                disabled={!myTurn || pending || view.cards[i] !== null}
                onClick={() => flip(i)}
                aria-label={t.memory.card(i + 1)}
              >
                <span class="m-card-inner">
                  <span class="m-card-back" />
                  <span class="m-card-front">{symbol !== null && <MemoryIcon symbol={symbol} />}</span>
                </span>
              </button>
            );
          })}
        </div>

        <aside class="card memory-side">
          <div class="score-row mine">
            <span class="score-name">{t.memory.you}</span>
            <strong class="score-value">{view.pairs.me}</strong>
          </div>
          <div class="score-row theirs">
            <span class="score-name">{state.opponent.displayName}</span>
            <strong class="score-value">{view.pairs.opponent}</strong>
          </div>
          <p class="muted">{t.memory.remaining(MEMORY_PAIRS - found)}</p>
        </aside>
      </div>
    </div>
  );
}
